import React, { useMemo, useState } from 'react';

import { Helmet } from 'react-helmet';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { Stack } from '@mui/material';

import SalesforceCampaignSchedulesModal from '~/components/Offers/Edit/SalesforceCampaignSchedulesModal';
import { SalesforceSurchargesModal } from '~/components/Vendors/SalesforceSurcharges/SalesforceSurchargesModal';

import { useGetProperty } from '~/hooks/apis/accommodation/useGetProperty';
import { useGetCampaignScheduleImportCandidates } from '~/hooks/apis/offers/useGetCampaignScheduleImportCandidates';
import { useGetOffer, useGetOpportunity, useGetOpportunityDeals } from '~/hooks/apis/offers/useGetOffer';
import { useGetOpportunityRelated } from '~/hooks/apis/offers/useGetOpportunityRelated';
import { useGetBlackoutDatesByRoomRate } from '~/hooks/apis/reservations/useGetBlackoutDatesByRoomRate';
import { useGetExistingSurchargesByRoomRate } from '~/hooks/apis/reservations/useGetExistingSurchargesByRoomRate';
import { useGetExtraGuestSurchargesByRoomRate } from '~/hooks/apis/reservations/useGetExtraGuestSurchargesByRoomRate';
import { useGetSalesforceSurcharges } from '~/hooks/apis/reservations/useGetSalesforceSurcharges';

import { type OfferBuilderProperty } from '../Content/PropertyInformation';

import { DealInformationTabCountProvider } from './DealInformationTabCounts';
import DealInformationToolbar from './DealInformationToolbar';
import { getDealInformationTab } from './constants';
import BlackoutDatesTab from './tabs/blackoutDates/BlackoutDatesTab';
import SalesforceBlackoutDatesWizardModal from './tabs/blackoutDates/SalesforceBlackoutDatesWizardModal';
import UploadStopsellDocumentModal from './tabs/blackoutDates/UploadStopsellDocumentModal';
import BundledExperiencesTab from './tabs/bundledExperiences/BundledExperiencesTab';
import CampaignSchedulesTab from './tabs/campaignSchedules/CampaignSchedulesTab';
import DealOptionsTab from './tabs/dealOptions/DealOptionsTab';
import DetailsTab from './tabs/details/DetailsTab';
import { type DetailSectionId, getDetailSection } from './tabs/details/types';
import ApplyEgsModal from './tabs/egs/ApplyEgsModal';
import EgsTab from './tabs/egs/EgsTab';
import InclusionsTab from './tabs/inclusions/InclusionsTab';
import SurchargesTab from './tabs/surcharges/SurchargesTab';
import {
  buildAppliedRoomsBySurchargeId,
  buildReservationSurchargeAssignments,
  getOfferRoomRateSelections,
  getOfferScopedRoomTypes,
} from './tabs/surcharges/surchargeUtils';
import type { DealInformationTab } from './types';

export default function OfferBuilderDealInformationPage() {
  const { id_offer: offerId } = useParams<{ id_offer: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const activeTab = getDealInformationTab(searchParams.get('tab'));
  const activeSection = getDetailSection(searchParams.get('section')) ?? getDetailSection('opportunity-info');
  const [isAddSurchargesModalOpen, setIsAddSurchargesModalOpen] = useState(false);
  const [isImportCampaignSchedulesModalOpen, setIsImportCampaignSchedulesModalOpen] = useState(false);
  const [isImportSalesforceBlackoutDatesModalOpen, setIsImportSalesforceBlackoutDatesModalOpen] = useState(false);
  const [isApplyEgsModalOpen, setIsApplyEgsModalOpen] = useState(false);
  const [isUploadStopsellDocumentModalOpen, setIsUploadStopsellDocumentModalOpen] = useState(false);
  const isDetailsTabActive = activeTab === 'details';
  const isBlackoutDatesTabActive = activeTab === 'blackout-dates';
  const isCampaignSchedulesTabActive = activeTab === 'campaign-schedules';
  const { data: offer, isLoading: isLoadingOffer, error: offerError } = useGetOffer(offerId);
  const opportunityId = offer?.id_salesforce_external ?? offerId;
  const propertyId = offer?.packages?.find((offerPackage) => offerPackage?.fk_property_id)?.fk_property_id;
  const isDealOptionsTabActive = activeTab === 'deal-options';
  const isBundledExperiencesTabActive = activeTab === 'bundled-experiences';
  const isEgsTabActive = activeTab === 'egs';
  const {
    data: opportunity,
    isLoading: isLoadingOpportunity,
    error: opportunityError,
  } = useGetOpportunity(opportunityId, {
    enabled: isDetailsTabActive || isDealOptionsTabActive || isBundledExperiencesTabActive || isBlackoutDatesTabActive,
  });
  const {
    data: opportunityDeals = [],
    isLoading: isLoadingOpportunityDeals,
    error: opportunityDealsError,
  } = useGetOpportunityDeals(opportunityId, { enabled: isDetailsTabActive });
  const {
    data: property,
    isLoading: isLoadingProperty,
    error: propertyError,
  } = useGetProperty<OfferBuilderProperty>({
    propertyId,
    enabled:
      (activeTab === 'surcharges' ||
        isDetailsTabActive ||
        isDealOptionsTabActive ||
        isBlackoutDatesTabActive ||
        isEgsTabActive) &&
      !!propertyId,
  });
  const {
    data: salesforceSurcharges = [],
    isLoading: isLoadingSurcharges,
    error: surchargesError,
  } = useGetSalesforceSurcharges(activeTab === 'surcharges' || isDealOptionsTabActive ? (offerId ?? null) : null);
  const {
    data: campaignScheduleImportCandidates,
    isLoading: isLoadingCampaignSchedules,
    error: campaignSchedulesError,
  } = useGetCampaignScheduleImportCandidates(offerId, { enabled: isCampaignSchedulesTabActive });
  const isLoadingCampaignSchedulesTab = isLoadingOffer || isLoadingCampaignSchedules;
  const campaignSchedulesTabError = offerError || campaignSchedulesError;
  const roomRateSelections = useMemo(
    () => getOfferRoomRateSelections(offer?.packages, property?.room_types),
    [offer?.packages, property?.room_types],
  );
  const offerScopedRoomTypes = useMemo(() => getOfferScopedRoomTypes(roomRateSelections), [roomRateSelections]);
  const {
    data: existingSurchargesByRoomRate = {},
    error: existingSurchargesError,
    isLoading: isLoadingExistingSurcharges,
    isFetching: isFetchingExistingSurcharges,
    refetch: refetchExistingSurcharges,
  } = useGetExistingSurchargesByRoomRate(
    propertyId ?? '',
    roomRateSelections,
    (activeTab === 'surcharges' || isDealOptionsTabActive) && !!propertyId,
  );
  const {
    data: extraGuestSurchargesByRoomRate = {},
    isLoading: isLoadingExtraGuestSurchargesByRoomRate,
    isFetching: isFetchingExtraGuestSurchargesByRoomRate,
    error: extraGuestSurchargesByRoomRateError,
  } = useGetExtraGuestSurchargesByRoomRate(
    propertyId ?? '',
    roomRateSelections,
    (isDealOptionsTabActive || isEgsTabActive) && !!propertyId,
  );
  const {
    data: blackoutDatesByRoomRate = {},
    error: blackoutDatesByRoomRateError,
    isLoading: isLoadingBlackoutDatesByRoomRate,
    isFetching: isFetchingBlackoutDatesByRoomRate,
  } = useGetBlackoutDatesByRoomRate(
    propertyId ?? '',
    roomRateSelections,
    (isDealOptionsTabActive || isBlackoutDatesTabActive) && !!propertyId,
  );
  const reservationSurcharges = useMemo(
    () =>
      buildReservationSurchargeAssignments({
        existingSurchargesByRoomRate,
        propertyId,
        roomRateSelections,
        surcharges: salesforceSurcharges,
        vendorId: offer?.vendor_account_id,
      }),
    [existingSurchargesByRoomRate, offer?.vendor_account_id, propertyId, roomRateSelections, salesforceSurcharges],
  );
  const appliedRoomsBySurchargeId = useMemo(
    () =>
      buildAppliedRoomsBySurchargeId({
        reservationSurcharges,
        surcharges: salesforceSurcharges,
      }),
    [reservationSurcharges, salesforceSurcharges],
  );
  const addSurchargesModalData =
    offer && offerId && propertyId && offerScopedRoomTypes.length && offer.vendor_account_id
      ? {
          offer,
          offerId,
          propertyId,
          roomTypes: offerScopedRoomTypes,
          vendorId: offer.vendor_account_id,
        }
      : undefined;
  const canAddSurcharges = !!addSurchargesModalData;
  const {
    data: opportunityRelated,
    isLoading: isLoadingOpportunityRelated,
    error: opportunityRelatedError,
  } = useGetOpportunityRelated(opportunityId, {
    enabled: isDealOptionsTabActive || isBundledExperiencesTabActive || isEgsTabActive,
  });

  const closeAddSurchargesModal = () => {
    setIsAddSurchargesModalOpen(false);
    refetchExistingSurcharges();
  };

  const updateSearchParam = (key: string, value: string) => {
    const nextSearchParams = new URLSearchParams(location.search);
    nextSearchParams.set(key, value);
    navigate({ pathname: location.pathname, search: nextSearchParams.toString() });
  };

  const handleTabChange = (tab: DealInformationTab) => {
    const nextSearchParams = new URLSearchParams(location.search);
    nextSearchParams.set('tab', tab);

    if (tab !== 'details') {
      nextSearchParams.delete('section');
    } else if (!getDetailSection(nextSearchParams.get('section'))) {
      nextSearchParams.set('section', 'opportunity-info');
    }

    navigate({ pathname: location.pathname, search: nextSearchParams.toString() });
  };

  const renderActiveTab = () => {
    switch (activeTab) {
      case 'details':
        return (
          <DetailsTab
            activeSectionId={activeSection?.id ?? 'opportunity-info'}
            dealCount={opportunityDeals.length}
            error={offerError || opportunityError || opportunityDealsError || propertyError}
            isLoading={isLoadingOffer || isLoadingOpportunity || isLoadingOpportunityDeals || isLoadingProperty}
            onSectionChange={(sectionId: DetailSectionId) => updateSearchParam('section', sectionId)}
            offer={offer}
            opportunity={opportunity}
            opportunityId={opportunityId}
            property={property}
          />
        );
      case 'surcharges':
        return (
          <SurchargesTab
            canAddSurcharges={canAddSurcharges}
            error={surchargesError}
            isLoading={isLoadingSurcharges}
            isLoadingReservationSurcharges={
              isLoadingOffer || isLoadingProperty || isLoadingExistingSurcharges || isFetchingExistingSurcharges
            }
            onAddSurcharges={() => setIsAddSurchargesModalOpen(true)}
            reservationSurcharges={reservationSurcharges}
            reservationSurchargesError={offerError || propertyError || existingSurchargesError}
            surcharges={salesforceSurcharges}
          />
        );
      case 'deal-options':
        return (
          <DealOptionsTab
            appliedRoomsBySurchargeId={appliedRoomsBySurchargeId}
            blackoutDatesByRoomRate={blackoutDatesByRoomRate}
            bundledExperiences={opportunityRelated?.bundled_experiences ?? []}
            deals={opportunityRelated?.deals ?? []}
            error={opportunityRelatedError}
            extraGuestSurchargesByRoomRate={extraGuestSurchargesByRoomRate}
            inclusions={opportunityRelated?.inclusions ?? []}
            isLoading={
              isLoadingOffer ||
              isLoadingOpportunityRelated ||
              isLoadingProperty ||
              isLoadingExistingSurcharges ||
              isFetchingExistingSurcharges ||
              isLoadingExtraGuestSurchargesByRoomRate ||
              isFetchingExtraGuestSurchargesByRoomRate ||
              isLoadingBlackoutDatesByRoomRate ||
              isFetchingBlackoutDatesByRoomRate
            }
            packages={offer?.packages ?? []}
            salesforceSurcharges={salesforceSurcharges}
          />
        );
      case 'inclusions':
        return <InclusionsTab />;
      case 'egs':
        return (
          <EgsTab
            canApplyEgs={!!propertyId && roomRateSelections.length > 0}
            error={offerError || opportunityRelatedError}
            extraGuestSurchargesByRoomRate={extraGuestSurchargesByRoomRate}
            onApplySalesforceEgs={() => setIsApplyEgsModalOpen(true)}
            isLoading={isLoadingOffer || isLoadingOpportunityRelated}
            isLoadingRoomRateEgs={
              isLoadingProperty || isLoadingExtraGuestSurchargesByRoomRate || isFetchingExtraGuestSurchargesByRoomRate
            }
            isLoadingRoomTypes={isLoadingProperty}
            propertyId={propertyId}
            roomRateEgsError={propertyError || extraGuestSurchargesByRoomRateError}
            roomTypes={offerScopedRoomTypes}
            roomTypesError={propertyError}
            surcharges={opportunityRelated?.extra_guest_surcharges ?? []}
            vendorId={offer?.vendor_account_id}
          />
        );
      case 'blackout-dates':
        return (
          <BlackoutDatesTab
            blackoutDates={opportunity?.black_out_dates ?? []}
            blackoutDatesByRoomRate={blackoutDatesByRoomRate}
            blackoutDatesByRoomRateError={blackoutDatesByRoomRateError}
            canApplyBlackoutDates={!!propertyId && roomRateSelections.length > 0}
            error={offerError || opportunityError || propertyError}
            isLoading={isLoadingOffer || isLoadingOpportunity || isLoadingProperty}
            isLoadingBlackoutDatesByRoomRate={isLoadingBlackoutDatesByRoomRate || isFetchingBlackoutDatesByRoomRate}
            onImportSalesforceBlackoutDates={() => setIsImportSalesforceBlackoutDatesModalOpen(true)}
            onUploadStopsellDocument={() => setIsUploadStopsellDocumentModalOpen(true)}
            propertyId={propertyId}
            roomRateSelections={roomRateSelections}
            roomTypeNames={property?.room_types?.map((roomType) => roomType.name) ?? []}
            vendorId={offer?.vendor_account_id}
          />
        );
      case 'bundled-experiences':
        return (
          <BundledExperiencesTab
            bundledExperiences={opportunityRelated?.bundled_experiences ?? []}
            deals={opportunityRelated?.deals ?? []}
            error={opportunityRelatedError || opportunityError}
            isLoading={isLoadingOpportunityRelated || isLoadingOpportunity}
          />
        );
      case 'campaign-schedules':
        return (
          <CampaignSchedulesTab
            brandSchedules={offer?.brand_schedules ?? []}
            error={campaignSchedulesTabError}
            importCandidates={campaignScheduleImportCandidates}
            isLoading={isLoadingCampaignSchedulesTab}
            offerId={offerId}
            onImportFromSalesforce={() => setIsImportCampaignSchedulesModalOpen(true)}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <Helmet>
        <title>Deal Information | Offer Builder</title>
      </Helmet>

      <DealInformationTabCountProvider>
        <Stack spacing={3}>
          <DealInformationToolbar activeTab={activeTab} onTabChange={handleTabChange} />
          {renderActiveTab()}
        </Stack>
      </DealInformationTabCountProvider>

      {isAddSurchargesModalOpen && addSurchargesModalData && (
        <SalesforceSurchargesModal
          closeModal={closeAddSurchargesModal}
          initialOfferId={addSurchargesModalData.offerId}
          isOpen
          offers={[addSurchargesModalData.offer]}
          propertyId={addSurchargesModalData.propertyId}
          roomTypes={addSurchargesModalData.roomTypes}
          vendorId={addSurchargesModalData.vendorId}
        />
      )}

      {offerId && (
        <SalesforceCampaignSchedulesModal
          error={campaignSchedulesTabError}
          importCandidates={campaignScheduleImportCandidates}
          isError={!!campaignSchedulesTabError}
          isLoading={isLoadingCampaignSchedulesTab}
          offerId={offerId}
          onClose={() => setIsImportCampaignSchedulesModalOpen(false)}
          open={isImportCampaignSchedulesModalOpen}
        />
      )}

      {isImportSalesforceBlackoutDatesModalOpen && propertyId && (
        <SalesforceBlackoutDatesWizardModal
          blackoutDates={opportunity?.black_out_dates ?? []}
          blackoutDatesByRoomRate={blackoutDatesByRoomRate}
          onClose={() => setIsImportSalesforceBlackoutDatesModalOpen(false)}
          propertyId={propertyId}
          roomRateSelections={roomRateSelections}
          vendorId={offer?.vendor_account_id ?? ''}
        />
      )}

      {/* Never opened from an errored load, but guarded here too: empty stored records read as nothing saved. */}
      {isApplyEgsModalOpen && propertyId && !extraGuestSurchargesByRoomRateError && (
        <ApplyEgsModal
          egsByRoomRateId={extraGuestSurchargesByRoomRate}
          onClose={() => setIsApplyEgsModalOpen(false)}
          propertyId={propertyId}
          roomRateSelections={roomRateSelections}
          roomTypes={offerScopedRoomTypes}
          surcharges={opportunityRelated?.extra_guest_surcharges ?? []}
          vendorId={offer?.vendor_account_id}
        />
      )}

      {isUploadStopsellDocumentModalOpen && propertyId && (
        <UploadStopsellDocumentModal
          blackoutDatesByRoomRate={blackoutDatesByRoomRate}
          onClose={() => setIsUploadStopsellDocumentModalOpen(false)}
          propertyId={propertyId}
          roomRateSelections={roomRateSelections}
        />
      )}
    </>
  );
}
